import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { dashboardApi } from "../api/DashboardApi";
import { GroupData } from "../components/dashboard/BasicInformation/BasicInformation";
import { IForm } from "../interfaces/university/form";
import { ITalksProps } from "../interfaces/university/talk";
import { API_URL, ENV } from "../settings";
import { getFileName } from "../utils/getFileName";

/*
  Data representation for White Labels (also shortened as WL). White Labels could be
    of two types: University and Event. Event, also refered as One-shot,
    is a simple type that should have only one group. This type represents
    real events with short time durations and with a period very well specified.
    University, also refered as Profile, is the most complete type. It
    allows to specify multiple groups. This type works like a channel, with multiple
    contents that will be available for a long time period, not necessarily specified.
    Attributes:
        groups: the groups shown on WL must be specified here.
        config: has the basic values of configuration of the WL
                (e.g primary and secondary colors).
        header: contains all values of WL header (e.g. title, date and time of
                the event).
        body: has all values of WL body (e.g. description, talkers,
              schedule and contents).
        footer: has all values of WL footer (e.g. social networks info and
                footer background color).
        slug:  a human-readable identifier. The slug will be used in the URL, to access this WL.
        owner: a FK for the WL owner.
        name: the name of the WL.
        is_active: boolean field indicating the status.
        kind: the type of whitelabel, it should be Event or University.
        logo: the logo image.
        banner: the banner image to personalize the WL.
        about_image: an image to be with the 'about' text field.
        additional_images: attribute to allow add more images and add more
                           possibilities to custom the wl.
        created_at: the timestamp of the WL creation.

    By: José Ribeiro
*/

type Manager = {
  username: string;
  name: string;
  avatar_url: string;
};

export type LoggedUser = {
  id: number;
  username: string;
  avatar_url: string;
  email: string;
  name: string;
  manager?: Manager;
};

type ChannelType = {
  username: string | undefined;
  data: any;
  talks: ITalksProps[];
  loading: boolean;
  errorPage: "OK" | "404" | "403";
  setErrorPage: (a: "OK" | "404" | "403") => void;
  groups: IGroup[];
  channel: string | undefined;
  draftData: any;
  owner: string | undefined;
  user: LoggedUser | null;

  title: string;
  setTitle: Dispatch<SetStateAction<string>>;
  orderOfTalks: "ASC" | "DESC" | "PLAYLIST";
  setOrderOfTalks: Dispatch<SetStateAction<"ASC" | "DESC" | "PLAYLIST">>;
  draggableGroups: GroupData[];
  setDraggableGroups: Dispatch<SetStateAction<any>>;

  appearenceTextColor: string;
  setAppearenceTextColor: Dispatch<SetStateAction<string>>;
  appearencePrimaryColor: string;
  setAppearencePrimaryColor: Dispatch<SetStateAction<string>>;
  appearenceSecondaryColor: string;
  setAppearenceSecondaryColor: Dispatch<SetStateAction<string>>;
  appearenceImageUrl: string;
  setAppearenceImageUrl: Dispatch<SetStateAction<string>>;
  appearenceImageFile: any;
  setAppearenceImageFile: Dispatch<SetStateAction<any>>;

  bannerDesktopUrl: string;
  setBannerDesktopUrl: Dispatch<SetStateAction<string>>;
  bannerDesktopFile: File | null;
  setBannerDesktopFile: Dispatch<SetStateAction<File | null>>;
  bannerMobileUrl: string;
  setBannerMobileUrl: Dispatch<SetStateAction<string>>;
  bannerMobileFile: File | null;
  setBannerMobileFile: Dispatch<SetStateAction<File | null>>;
  banners: any;
  setBanners: any;
  bannersFile: any;
  setBannersFile: any;
  descriptionTitle: string;
  setDescriptionTitle: Dispatch<SetStateAction<string>>;
  descriptionText: string;
  setDescriptionText: Dispatch<SetStateAction<string>>;
  descriptionImageUrl: string;
  setDescriptionImageUrl: Dispatch<SetStateAction<string>>;
  descriptionImageFile: any;
  setDescriptionImageFile: Dispatch<SetStateAction<File | null>>;

  newsletterCall: string;
  setNewsletterCall: Dispatch<SetStateAction<string>>;
  setNewsLetterIsActive: Dispatch<SetStateAction<boolean>>;
  newsLetterIsActive: boolean;
  newsletterText: string;
  setNewsletterText: Dispatch<SetStateAction<string>>;
  newsletterPrimaryColor: string;
  setNewsletterPrimaryColor: Dispatch<SetStateAction<string>>;
  newsletterSecondaryColor: string;
  setNewsletterSecondaryColor: Dispatch<SetStateAction<string>>;

  footerColor: string;
  setFooterColor: Dispatch<SetStateAction<string>>;
  footerFacebook: IWLDataSocial;
  setFooterFacebook: Dispatch<SetStateAction<any>>;
  footerTwitter: IWLDataSocial;
  setFooterTwitter: Dispatch<SetStateAction<any>>;
  footerLinkedin: IWLDataSocial;
  setFooterLinkedin: Dispatch<SetStateAction<any>>;
  footerYoutube: IWLDataSocial;
  setFooterYoutube: Dispatch<SetStateAction<any>>;
  footerInstagram: IWLDataSocial;
  setFooterInstagram: Dispatch<SetStateAction<any>>;

  footerSpotify: IWLDataSocial;
  setFooterSpotify: Dispatch<SetStateAction<any>>;
  footerSite: IWLDataSocial;
  setFooterSite: Dispatch<SetStateAction<any>>;

  footerTextColor: string;
  setFooterTextColor: Dispatch<SetStateAction<string>>;
};

export interface BannerProps {
  name: string;
  desktop_file: any;
  mobile_file: any;
  desktop_url: string;
  mobile_url: string;
  isActive: boolean;
  kind?: string;
  whitelabel_id?: number;
  desktop_name?: string;
  mobile_name?: string;
}


export interface ISubgroup {
  background: string;
  banner: string;
  banner_pre: string;
  cover: string;
  description: string;
  id: number;
  name: string;
  slug: string;
  talks: ITalksProps[];
}

export interface IGroup {
  description?: string;
  cover?: string;
  slug: string;
  background?: string;
  talks: ITalksProps[];
  is_public: boolean;
  subgroups: ISubgroup[];
  banner: string;
  id: number;
  banner_pre: string;
  name: string;
  url: string;
  ticket_price: number;
  form: IForm;
  owner_username: string;
}

interface IWLDataSocial {
  url: string;
  active: boolean;
}

interface IWLDataNewsletter {
  title: string;
  description: string;
  colorPrimary: string;
  colorSecondary: string;
}

interface IWLData {
  body: {
    about: string;
    eventAbout?: {
      title?: string;
      description?: string;
    };
  };

  config: {
    about_image_name?: string | null;
    banner_desktop_name?: string | null;
    banner_mobile_name?: string | null;
    bannersMobile: string[];

    colors: {
      colorText?: string | null;
      colorPrimary?: string | null;
      colorSecondary?: string | null;
    };

    eventAbout: {
      title: string;
      description: string;
    };

    newsletter: {
      colorPrimary: string;
      colorSecondary: string;
      description: string;
      title: string;
      showNewsletter: boolean;
    };

    primary_color: string;
    secondary_color: string;
    showAditionalBanner: boolean;
    showNewsletter: boolean;

    text: {
      dark: string;
      light: string;
    };

    text_color: string;
    wrapTalks: boolean;
  };

  footer: {
    footerTextColor: SetStateAction<string>;
    color: string;
    social: {
      spotify: SetStateAction<IWLDataSocial>;
      site: SetStateAction<IWLDataSocial>;
      twitter: IWLDataSocial;
      youtube: IWLDataSocial;
      facebook: IWLDataSocial;
      linkedin: IWLDataSocial;
      instagram: IWLDataSocial;
    };
  };

  header: { title: string };

  images: {
    banners: any;
    about_image: string;
    additional_images: any[];
    banner_desktop: string;
    banner_mobile: string | null;
    logo: string;
    newsletter: IWLDataNewsletter[];
  };

  groups?: GroupData[];

  kind: string;
  name: string;
  owner_name: string;
  order_talks: "ASC" | "DESC" | "PLAYLIST";
}

// interface BannersData {
//   url: string;
//   name: string;
//   whitelabel_id: number;
// }

const DATA_DEFAULT_VALUE = {
  text: {},
  images: {},
  about: {},
  name: "",
  footer: {},
  newsletter: {},
  colors: {},
};

const TALKS_DEFAULT_VALUE: ITalksProps[] = [];
const USERNAME_DEFAULT_VALUE = "";
const OWNER_DEFAULT_VALUE = "";
const LOADING_DEFAULT_VALUE = true;
const ERROR_PAGE_DEFAULT_VALUE = "OK";
const GROUPS_DEFAULT_VALUE: IGroup[] = [];
const CHANNEL_DEFAULT_VALUE = "";
const USER_DEFAULT_VALUE = null;

export const ChannelContext = createContext<ChannelType>({
  data: DATA_DEFAULT_VALUE,
  talks: TALKS_DEFAULT_VALUE,
  username: USERNAME_DEFAULT_VALUE,
  loading: LOADING_DEFAULT_VALUE,
  errorPage: ERROR_PAGE_DEFAULT_VALUE,
  setErrorPage: () => { },
  groups: GROUPS_DEFAULT_VALUE,
  channel: CHANNEL_DEFAULT_VALUE,
  draftData: DATA_DEFAULT_VALUE,
  owner: OWNER_DEFAULT_VALUE,
  user: USER_DEFAULT_VALUE,
  title: "",
  setTitle: () => { },
  orderOfTalks: "ASC",
  setOrderOfTalks: () => { },
  draggableGroups: [],
  setDraggableGroups: () => { },
  banners: [],
  setBanners: () => { },
  bannersFile: [],
  setBannersFile: () => { },
  bannerDesktopUrl: "",
  setBannerDesktopFile: () => { },
  bannerDesktopFile: null,
  setBannerDesktopUrl: () => { },
  bannerMobileFile: null,
  setBannerMobileFile: () => { },
  bannerMobileUrl: "",
  setBannerMobileUrl: () => { },

  appearenceTextColor: "",
  setAppearenceTextColor: () => { },
  appearencePrimaryColor: "",
  setAppearencePrimaryColor: () => { },
  appearenceSecondaryColor: "",
  setAppearenceSecondaryColor: () => { },
  appearenceImageUrl: "",
  setAppearenceImageUrl: () => { },
  appearenceImageFile: {},
  setAppearenceImageFile: () => { },

  descriptionTitle: "",
  setDescriptionTitle: () => { },
  descriptionText: "",
  setDescriptionText: () => { },
  descriptionImageUrl: "",
  setDescriptionImageUrl: () => { },
  descriptionImageFile: {},
  setDescriptionImageFile: () => { },

  newsletterCall: "",
  setNewsletterCall: () => { },
  newsletterText: "",
  setNewsletterText: () => { },
  newsletterPrimaryColor: "",
  setNewsletterPrimaryColor: () => { },
  newsletterSecondaryColor: "",
  setNewsletterSecondaryColor: () => { },
  footerColor: "",
  setFooterColor: () => { },
  footerFacebook: {
    url: "",
    active: false,
  },
  setFooterFacebook: () => { },
  footerTwitter: {
    url: "",
    active: false,
  },
  setFooterTwitter: () => { },
  footerLinkedin: {
    url: "",
    active: false,
  },
  setFooterLinkedin: () => { },
  footerYoutube: {
    url: "",
    active: false,
  },
  setFooterYoutube: () => { },
  footerInstagram: {
    url: "",
    active: false,
  },
  setFooterInstagram: () => { },
  setNewsLetterIsActive: () => { },
  newsLetterIsActive: false,

  footerSpotify: {
    url: "",
    active: false,
  },
  setFooterSpotify: () => { },
  footerSite: {
    url: "",
    active: false,
  },
  setFooterSite: () => { },

  footerTextColor: "",
  setFooterTextColor: () => { },
});

const ChannelContextProvider: React.FC = ({ children }) => {
  const talks: ITalksProps[] = TALKS_DEFAULT_VALUE;

  const [data, setData] = useState<any>(DATA_DEFAULT_VALUE);
  const [draftData, setDraftData] = useState<any>(DATA_DEFAULT_VALUE);
  const [groups, setGroups] = useState<IGroup[]>(GROUPS_DEFAULT_VALUE);
  const [username, setUsername] = useState<string | undefined>(
    USERNAME_DEFAULT_VALUE
  );
  const [owner, setOwner] = useState<string | undefined>(OWNER_DEFAULT_VALUE);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorPage, setErrorPage] = useState<"OK" | "404" | "403">("OK");
  const channel = window.location.pathname.replace("/", "").split("/").shift();
  const [user, setUser] = useState<LoggedUser | null>(null);

  // Dashboard Customization
  const [title, setTitle] = useState<string>("");
  const [orderOfTalks, setOrderOfTalks] = useState<"ASC" | "DESC" | "PLAYLIST">("ASC");
  const [draggableGroups, setDraggableGroups] = useState<GroupData[]>([]);

  // Banners
  const [bannerDesktopUrl, setBannerDesktopUrl] = useState<string>("");
  const [bannerDesktopFile, setBannerDesktopFile] = useState<File | null>(null);
  const [bannerMobileUrl, setBannerMobileUrl] = useState<string>("");
  const [bannerMobileFile, setBannerMobileFile] = useState<File | null>(null);
  const [bannersFile, setBannersFile] = useState([]);
  const [banners, setBanners] = useState([
    {
      name: "first",
      desktop_file: "",
      desktop_url: "",
      desktop_name: "",
      mobile_file: "",
      mobile_url: "",
      mobile_name: "",
      isActive: true,
    },
    {
      name: "second",
      desktop_file: "",
      desktop_url: "",
      desktop_name: "",
      mobile_file: "",
      mobile_url: "",
      mobile_name: "",
      isActive: false,
    },
    {
      name: "third",
      desktop_file: "",
      desktop_url: "",
      desktop_name: "",
      mobile_file: "",
      mobile_url: "",
      mobile_name: "",
      isActive: false,
    },
  ]);

  //Appearence
  const [appearenceTextColor, setAppearenceTextColor] =
    useState<string>("#000000");
  const [appearencePrimaryColor, setAppearencePrimaryColor] =
    useState<string>("#000000");
  const [appearenceSecondaryColor, setAppearenceSecondaryColor] =
    useState<string>("#000000");
  const [appearenceImageUrl, setAppearenceImageUrl] = useState<string>("");
  const [appearenceImageFile, setAppearenceImageFile] = useState<any>({});

  //Description Block
  const [descriptionTitle, setDescriptionTitle] = useState<string>("");
  const [descriptionText, setDescriptionText] = useState<string>("");
  const [descriptionImageUrl, setDescriptionImageUrl] = useState<string>("");
  const [descriptionImageFile, setDescriptionImageFile] = useState<any>({});
  const [newsLetterIsActive, setNewsLetterIsActive] = useState<boolean>(false);
  const [newsletterCall, setNewsletterCall] = useState<string>("");
  const [newsletterText, setNewsletterText] = useState<string>("");
  const [newsletterPrimaryColor, setNewsletterPrimaryColor] =
    useState<string>("");
  const [newsletterSecondaryColor, setNewsletterSecondaryColor] =
    useState<string>("");
  const [footerColor, setFooterColor] = useState<string>("");
  const [footerFacebook, setFooterFacebook] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerTwitter, setFooterTwitter] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerLinkedin, setFooterLinkedin] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerInstagram, setFooterInstagram] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerYoutube, setFooterYoutube] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerSite, setFooterSite] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerSpotify, setFooterSpotify] = useState<IWLDataSocial>({
    url: "",
    active: false,
  });
  const [footerTextColor, setFooterTextColor] = useState<string>("");

  const mode: "cors" | "navigate" | "no-cors" | "same-origin" | undefined =
    "cors";
  const credentials: "same-origin" | "include" | "omit" | undefined = "include";

  useEffect(() => {
    const isOnListPage = window.location.pathname.includes("list-white-labels");

    const isOnInitialPage = window.location.pathname.includes("welcome");

    const isFormPage = window.location.pathname.includes("formulario");

    if (isFormPage) {
      setLoading(false);
      return
    };

    setUsername(channel);

    let config: any = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    if (ENV === "staging") {
      config.headers["Authorization"] = "Basic ZXZlbnRpYWxzOnczYjFuNHIhQCM=";
      config.credentials = credentials;
      config.mode = mode;
    }

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    const isOnEditPage = window.location.pathname.includes(
      "dashboard-customization"
    );

    const isDraft =
      ("preview" in params && params.preview === "true") || isOnEditPage;

    let url = `${API_URL}/api/whitelabel/${channel}/layout/`;

    if (isOnListPage || isOnInitialPage) {
      setLoading(false);
    }

    if (isDraft) {
      url += "?draft=true";
    }

    if (!isOnListPage && !isOnInitialPage) {
      fetch(url, config)
        .then((res) => {
          if (res.status !== 200) {
            setErrorPage("404");
            throw new Error("err");
          }
          return res.json();
        })
        .then((data: IWLData) => {
          if (isDraft) {
            setDraftData(data);
          }
          let firstBanner = { isActive: true };
          let secondBanner = { isActive: false };
          let thirdBanner = { isActive: false };
        
          for (const banner of data.images.banners) {
            const type = banner.kind.split("_")[1];
            const name = banner.kind.split("_")[0];

            const newBanner = {
              name: name,
              [`${type}_url`]: banner.url,
              [`${type}_file`]: "",
              [`${type}_name`]: banner.name,
              whitelabel_id: banner.whitelabel_id,
              kind: banner.kind,
            };

            if (name === "first") {
              firstBanner = { ...firstBanner, ...newBanner, isActive: true };
            }

            if (name === "second") {
              const isActive =
                secondBanner?.isActive === true ? true : !!banner.url;

              secondBanner = {
                ...secondBanner,
                ...newBanner,
                isActive,
              };
            }

            if (name === "third") {
              const isActive =
                thirdBanner?.isActive === true ? true : !!banner.url;

              thirdBanner = {
                ...thirdBanner,
                ...newBanner,
                isActive,
              };
            }
          }

          setOwner(data?.owner_name);

          setData(data);
          setTitle(data.name);
          setOrderOfTalks(data.order_talks);

          if (
            data.images.banner_desktop &&
            data.images.banner_desktop.includes(
              "https://static-stg.eventials.com/media/"
            )
          ) {
            setBannerDesktopUrl(data.images.banner_desktop);
            setBannerDesktopFile({
              name: getFileName(data.images.banner_desktop),
            } as File);
          } else {
            setBannerDesktopUrl(data.images.banner_desktop);
            setBannerDesktopFile({ name: data.images.banner_desktop } as File);
          }

          if (
            data.images.banner_mobile &&
            data.images.banner_mobile.includes(
              "https://static-stg.eventials.com/media/"
            )
          ) {
            setBannerMobileUrl(data?.images?.banner_mobile);
            setBannerMobileFile({
              name: getFileName(data?.images?.banner_mobile),
            } as File);
          } else {
            setBannerMobileUrl("");
            setBannerMobileFile({ name: data?.images?.banner_mobile } as File);
          }

          if (
            data?.images?.about_image &&
            data?.images?.about_image.includes(
              "https://static-stg.eventials.com/media/"
            )
          ) {
            setDescriptionImageUrl(data?.images?.about_image);
            setDescriptionImageFile({
              name: getFileName(data?.images?.about_image),
            } as File);
          } else {
            setDescriptionImageUrl("");
            setDescriptionImageFile({ name: data.images.about_image } as File);
          }

          setAppearenceTextColor(data?.config?.colors?.colorText ?? "#000000");
          setAppearencePrimaryColor(
            data?.config?.colors?.colorPrimary ?? "#000000"
          );
          setAppearenceSecondaryColor(
            data?.config?.colors?.colorSecondary ?? "#000000"
          );
          setAppearenceImageUrl(data?.images?.logo ?? "");
          setDescriptionTitle(data?.body?.eventAbout?.title ?? "");
          setDescriptionText(data?.body?.eventAbout?.description ?? "");
          setDescriptionImageUrl(data?.images?.about_image ?? "");
          setNewsletterCall(data?.config?.newsletter?.title ?? "");
          setNewsletterText(data?.config?.newsletter?.description ?? "");
          setNewsletterPrimaryColor(data?.config?.newsletter?.colorPrimary);
          setNewsletterSecondaryColor(data?.config?.newsletter?.colorSecondary);
          setNewsLetterIsActive(data?.config?.newsletter?.showNewsletter);
          setFooterColor(data?.footer?.color);
          setFooterFacebook(data?.footer?.social?.facebook);
          setFooterInstagram(data?.footer?.social?.instagram);
          setFooterLinkedin(data?.footer?.social?.linkedin);
          setFooterTwitter(data?.footer?.social?.twitter);
          setFooterYoutube(data?.footer?.social?.youtube);
          setFooterTextColor(data?.footer?.footerTextColor);
          setFooterSite(data?.footer?.social?.site);
          setFooterSpotify(data?.footer?.social?.spotify);
          setBanners([
            { ...banners[0], ...firstBanner },
            { ...banners[1], ...secondBanner },
            { ...banners[2], ...thirdBanner },
          ]);

          setDraggableGroups(data?.groups ? [...data.groups] : []);

          document.title = data?.name;
        })
        .catch((err) => {
          console.log(err);
        });
    }

    let urlWebinars = `${API_URL}/api/whitelabel/${channel}/webinars/`;
    if (isDraft) {
      urlWebinars += "?draft=true";
    }

    if (!isOnListPage && !isOnInitialPage) {
      fetch(urlWebinars, config)
        .then((res) => {
          if (res.status !== 200) {
            throw new Error("err");
          }
          return res.json();
        })
        .then((talks) => {
          setGroups(talks.groups);
        })
        .catch((err) => {
          console.log(err);
        });

      if (talks && data) {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    }

    if (isOnEditPage || isOnListPage) {
      dashboardApi.getAuthentication().then((response: LoggedUser | string) => {
        if (typeof (response) !== "string") {
          setUser(response)
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && owner) {
      if (owner !== user.username) {
        setErrorPage("403");
      }
    }
  }, [owner, user]);

  return (
    <ChannelContext.Provider
      value={{
        data,
        talks,
        username,
        loading,
        errorPage,
        setErrorPage,
        groups,
        channel,
        draftData,
        owner,
        user,
        title,
        setTitle,
        orderOfTalks,
        setOrderOfTalks,
        draggableGroups,
        setDraggableGroups,
        bannerDesktopFile,
        setBannerDesktopFile,
        bannerDesktopUrl,
        setBannerDesktopUrl,
        bannerMobileFile,
        setBannerMobileFile,
        bannerMobileUrl,
        setBannerMobileUrl,
        appearenceTextColor,
        setAppearenceTextColor,
        appearencePrimaryColor,
        setAppearencePrimaryColor,
        appearenceSecondaryColor,
        setAppearenceSecondaryColor,
        appearenceImageUrl,
        setAppearenceImageUrl,
        appearenceImageFile,
        setAppearenceImageFile,
        descriptionImageFile,
        setDescriptionImageFile,
        descriptionImageUrl,
        setDescriptionImageUrl,
        descriptionTitle,
        setDescriptionTitle,
        descriptionText,
        setDescriptionText,
        newsletterCall,
        setNewsletterCall,
        newsletterText,
        setNewsletterText,
        newsletterPrimaryColor,
        setNewsletterPrimaryColor,
        newsletterSecondaryColor,
        setNewsletterSecondaryColor,
        footerColor,
        setFooterColor,
        footerFacebook,
        setFooterFacebook,
        footerTwitter,
        setFooterTwitter,
        footerLinkedin,
        setFooterLinkedin,
        footerYoutube,
        setFooterYoutube,
        footerInstagram,
        setFooterInstagram,
        footerSite,
        setFooterSite,
        footerSpotify,
        setFooterSpotify,
        banners,
        setBanners,
        bannersFile,
        setBannersFile,
        setNewsLetterIsActive,
        newsLetterIsActive,
        footerTextColor,
        setFooterTextColor,
      }}
    >
      {children}
    </ChannelContext.Provider>
  );
};

export default ChannelContextProvider;
